// --- React ---
import React, { useMemo } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 · 테마 ---
import { useTheme } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import { DataTable } from '@/components/common/DataTable';

// --- 타입 ---
import type { ColumnDef } from '@tanstack/react-table';
import type { PushNotificationHistoryItem } from '@/api/monitoring/useMonitoring';

// --- 유틸 ---
import {
  getPushNotificationAccentColor,
  getPushNotificationSeverity,
} from '@/utils/monitoringGroupColors';

// ==================== 타입 정의 ====================

interface PushNotificationHistoryGridProps {
  data: PushNotificationHistoryItem[];
  isLoading?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  onRowClick?: (serialNumber: string) => void;
}

// ==================== 컴포넌트 ====================

export const PushNotificationHistoryGrid: React.FC<PushNotificationHistoryGridProps> = ({
  data,
  isLoading = false,
  enableFiltering = true,
  enablePagination = true,
  onRowClick,
}) => {
  const { theme } = useTheme();
  const { i18n, t } = useTranslation('common');

  // ==================== 테이블 컬럼 정의 ====================

  // 고유한 notification_type 목록
  // const notificationTypes = useMemo(() => {
  //   const types = new Set(data.map(item => item.notification_type));
  //   return Array.from(types).sort();
  // }, [data]);

  const columns: ColumnDef<PushNotificationHistoryItem>[] = useMemo(
    () => [
      {
        accessorKey: 'sent_at',
        header: 'sent at',
        cell: ({ row }) => {
          const date = row.original.sent_at;
          try {
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
          } catch {
            return date;
          }
        },
        size: 150,
        enableColumnFilter: enableFiltering,
        meta: {
          style: { width: '12%', minWidth: '150px' },
        },
      },
      // {
      //   accessorKey: 'notification_type',
      //   header: 'type',
      //   cell: ({ row }) => {
      //     const type = row.original.notification_type;
      //
      //     // 백엔드에서 저장한 notification_type 값에 따라 매핑
      //     const typeMap: Record<string, string> = {
      //       device_connected: t(i18nKeys.common.push_notification_type_connect),
      //       device_disconnected: t(i18nKeys.common.push_notification_type_disconnect),
      //       alarm: t(i18nKeys.common.push_notification_type_alarm),
      //       alarm_end: t(i18nKeys.common.push_notification_type_alarm_end),
      //       warning: t(i18nKeys.common.push_notification_type_warning),
      //       device_info: t(i18nKeys.common.push_notification_type_device_info),
      //       test: t(i18nKeys.common.push_notification_type_test),
      //     };
      //
      //     // typeMap에 있으면 번역된 값 반환, 없으면 원본 반환
      //     if (typeMap[type]) {
      //       return typeMap[type];
      //     }
      //
      //     // i18n 키 형태로 저장된 경우 처리 (예: push_notification_type_alarm)
      //     if (type?.startsWith('push_notification_type_')) {
      //       const key = type.replace('push_notification_type_', '');
      //       const keyMap: Record<string, string> = {
      //         connect: t(i18nKeys.common.push_notification_type_connect),
      //         disconnect: t(i18nKeys.common.push_notification_type_disconnect),
      //         alarm: t(i18nKeys.common.push_notification_type_alarm),
      //         alarm_end: t(i18nKeys.common.push_notification_type_alarm_end),
      //         warning: t(i18nKeys.common.push_notification_type_warning),
      //         device_info: t(i18nKeys.common.push_notification_type_device_info),
      //         test: t(i18nKeys.common.push_notification_type_test),
      //       };
      //       return keyMap[key] || type;
      //     }
      //
      //     return type;
      //   },
      //   size: 120,
      // },
      // {
      //   accessorKey: 'serial_number',
      //   header: 'serial number',
      //   cell: ({ row }) => row.original.serial_number || '-',
      //   size: 140,
      //   minSize: 120,
      //   maxSize: 180,
      // },
      // {
      //   accessorKey: 'device_name',
      //   header: '기기명',
      //   cell: ({ row }) => row.original.device_name || '-',
      //   size: 150,
      // },
      // {
      //   accessorKey: 'company_name',
      //   header: 'company',
      //   cell: ({ row }) => row.original.company_name || '-',
      //   size: 60,
      // },
      // {
      //   accessorKey: 'site_name',
      //   header: 'site',
      //   cell: ({ row }) => row.original.site_name || '-',
      //   size: 60,
      // },

      {
        accessorKey: 'title',
        header: 'title',
        cell: ({ row }) => {
          const d = row.original.data || {};
          const sev = getPushNotificationSeverity(
            d as Record<string, unknown>,
            row.original.notification_type
          );
          const accent = getPushNotificationAccentColor(theme.colors, sev);
          // i18n_title 키가 있으면 현재 언어로 번역, 없으면 저장된 title 폴백
          const i18nKey = d.i18n_title as string | undefined;
          const text = i18nKey ? t(i18nKey) : row.original.title || '-';
          return (
            <span
              style={{
                color: accent,
                fontWeight: sev === 'neutral' ? undefined : 600,
              }}
            >
              {text}
            </span>
          );
        },
        size: 120,
        enableColumnFilter: enableFiltering,
        meta: {
          style: { width: '12%', minWidth: '120px' },
        },
      },
      {
        accessorKey: 'body',
        header: 'detail',
        cell: ({ row }) => {
          const d = row.original.data || {};
          const i18nKey = d.i18n_body as string | undefined;

          if (i18nKey) {
            // data에서 interpolation 변수 추출 (camelCase / snake_case 모두 지원)
            const str = (v: unknown) => (typeof v === 'string' ? v : '');
            const vars: Record<string, string> = {
              serialNumber: str(d.serialNumber ?? d.serial_number),
              siteName: str(d.siteName ?? d.site_name),
              workerName: str(d.workerName ?? d.worker_name),
              sensorName: str(d.sensorName ?? d.sensor_name),
              alarmName: str(d.alarmName ?? d.alarm_name),
              warningReason: str(d.warningReason ?? d.warning_reason),
              companyName: str(d.companyName ?? d.company_name),
            };

            let translated = t(i18nKey, vars);

            // 연결 끊김 사유 추가 (disconnect_reason_text가 없으면 코드로 번역)
            if (d.disconnect_reason) {
              const reasonKey = `disconnect_reason_${d.disconnect_reason}`;
              const reasonText = t(reasonKey) !== reasonKey ? t(reasonKey) : d.disconnect_reason;
              const suffix = d.disconnect_message
                ? `${reasonText}: ${d.disconnect_message}`
                : reasonText;
              translated += ` (${suffix})`;
            }

            const sev = getPushNotificationSeverity(
              d as Record<string, unknown>,
              row.original.notification_type
            );
            const accent = getPushNotificationAccentColor(theme.colors, sev);
            return (
              <span
                style={{
                  color: accent,
                  fontWeight: sev === 'neutral' ? undefined : 500,
                }}
              >
                {translated}
              </span>
            );
          }

          const sev = getPushNotificationSeverity(
            (row.original.data || {}) as Record<string, unknown>,
            row.original.notification_type
          );
          const accent = getPushNotificationAccentColor(theme.colors, sev);
          const fallback = row.original.body || '-';
          return (
            <span
              style={{
                color: accent,
                fontWeight: sev === 'neutral' ? undefined : 500,
              }}
            >
              {fallback}
            </span>
          );
        },
        size: 600,
        enableColumnFilter: enableFiltering,
        meta: {
          style: { width: '64%', minWidth: '400px' },
        },
      },
    ],
    [theme.colors, t, i18n.language, enableFiltering]
  );

  // ==================== 렌더링 ====================

  return (
    <>
      <style>{`
        /* PushNotificationHistoryGrid 테이블 셀 패딩 조정 */
        .push-notification-history-table td {
          padding: ${theme.spacing[2]}px ${theme.spacing[4]}px !important;
          line-height: 1.4;
        }
        .push-notification-history-table th {
          padding: ${theme.spacing[2]}px ${theme.spacing[4]}px !important;
        }
      `}</style>
      <DataTable
        data={data}
        columns={columns}
        enablePagination={enablePagination}
        pageSize={20}
        enableSorting={true}
        enableFiltering={enableFiltering}
        isLoading={isLoading}
        emptyMessage="Push notification 이력이 없습니다."
        enableExcelExport={false}
        excelFileName="push_notification_history"
        className="push-notification-history-table"
        onRowClick={row => {
          if (onRowClick && row.serial_number) {
            onRowClick(row.serial_number);
          }
        }}
      />
    </>
  );
};
